/* ═══════════════════════════════════════════════════════════════════════════════
   SHARED PAYROLL COMPUTE ENGINE
   ─────────────────────────────
   Extracted from PayrollPage.js so it can be called by:
     1. PayrollPage (for display)
     2. The IS orchestrator (for cross-module linkage)

   Inputs:
     emps     — array of employee objects with monthlyData, department, state, etc.
     taxCfg   — tax configuration object (SS, Medicare, FUTA, SUTA rates/caps)

   Outputs:
     { ed, dd, cd, g, isLines }
     ed = per-employee detail
     dd = per-department totals (the tenant's OWN department_mappings names)
     cd = RETAINED BUT NO LONGER POPULATED — see the note at the removal site below.
          It is returned as an empty object so consumers need no existence check.
     g  = grand totals
     isLines = mapped to IS line keys for direct consumption by computeIS
   ═══════════════════════════════════════════════════════════════════════════════ */

import { IS_CATEGORY_DISPLAY_TO_SLUG } from './lineRoles';
import { buildPayrollIsLineKey } from './payrollKeys';

import { deriveHeadcount } from './headcount';
// ★ B1 — THE ONE comp-cycle anchor derivation. This engine carried its own copy of
// deriveBonusAnchor / deriveCommAnchor / FREQ_MONTHS; compCycle.js was already the shared,
// exported home (PayrollPage imports it to SHOW the operator the inherited anchor). Now the
// value shown and the value BOOKED come from the same function, so they cannot drift.
import { deriveBonusAnchor, deriveCommAnchor, FREQ_MONTHS } from '../utils/compCycle';
// Bi-weekly payroll, step 3b: the accrued-payroll balance. accruedSalaryByMonth is the ONE derivation (the same pass that
// fixes the salary's cents); accruesPayroll / accrualCoversTax are the two questions asked of the company's schedule.
import { accruedSalaryByMonth } from './salarySeries';
import { accruesPayroll, accrualCoversTax } from './accruedPayroll';
import { payCalendarProblem } from './paySchedule';
import { accruesCommissions, commissionAccrualBasis } from './commissionAccrual';   // commission ship 4: the accrual answer's own rules
const r2 = n => Math.round(n * 100) / 100;

/* SEVERANCE + COBRA -- THE ONE RESOLVER. Step 3.
 *
 * The operator's answer lives on the employee row (severance_mode 'weeks'|'amount',
 * severance_value, severance_cobra_amount -- nullable, defaultless). ScenariosPage sets the
 * SAME FIELDS on its cloned employee rather than mutating monthlyData, so the live model and
 * a scenario flow through this function and cannot drift apart.
 *
 * WEEKS = annual / 52 * weeks. NOT /24, NOT /26: a week of pay is a 52nd of a year.
 * AMOUNT = the stored dollars, verbatim.
 *
 * THE N-B DISCIPLINE: null / undefined / '' are UNSET; a finite 0 is an ANSWER and must
 * survive as 0. Never `|| 0` and never `|| 1` -- that is the 09-15 take-rate defect, where a
 * deliberate 0 was silently replaced by the maximum of the field's range.
 *
 * COBRA IS RETURNED SEPARATELY AND DELIBERATELY: it is NOT compensation. It never enters
 * totalComp, so it is never taxed, never consumes a wage base, and creates no head.
 */
export function severanceFor(emp) {
    const e = emp || {};
    const unset = v => v === undefined || v === null || v === '';
    const num = v => { if (unset(v)) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
    const mode = e.severance_mode ?? e.severanceMode ?? null;
    const value = num(e.severance_value ?? e.severanceValue);
    const cobraRaw = num(e.severance_cobra_amount ?? e.severanceCobraAmount);
    let severance = 0;
    if (value !== null && (mode === 'weeks' || mode === 'amount')) {
        const annual = Number(e.salary ?? e.annual_salary) || 0;
        severance = mode === 'weeks' ? r2((annual / 52) * value) : r2(value);
    }
    return { severance, cobra: cobraRaw === null ? 0 : r2(cobraRaw), mode, value };
}

// ★ C-11 (Build B2, shape A): the employer benefits cost is Σ(benefit_components values) — the ONE
// benefit-sum in the codebase (ScenariosPage imports it). The engine reads benefit_components
// DIRECTLY; the 4 typed columns (med/dental/vision/k401) are dead. Summing VALUES (never matching
// NAMES) is what kills the ghost-key problem by construction — any component name is counted.
export function sumBenefitComponents(bc) {
  return r2(Object.values(bc || {}).reduce((s, v) => s + (Number(v) || 0), 0));
}

// ★ SWEEP B2-ii: the hardcoded 2024-2026 ALL_M window is DELETED. It was Semitek's
// model window used as a back-compat default; every caller passes allMonths
// (runFullModel always does) and no page imports the export — verified. An
// absent window now yields an empty one (honest-empty), not a foreign calendar.

/* ⛔⛔ REMOVED — `LEGACY_SEMITEK_CATS` / `LEGACY_SEMITEK_DEPTS`, AND WHY, IN THE PAST TENSE.
 *
 * They were Semitek's four is_categories ({cogs,sm,ga,rnd}) and its six department names
 * ("Customer Success", "Content", "Growth", "General & Administrative", "Product",
 * "Engineering"), and they EXECUTED UNCONDITIONALLY for every tenant on every model run:
 * `DD_KEYS` seeded `dd` with the six names before the tenant's own, `cd` was created from
 * the four ids, `dd` rolled into `cd`, `cd` rolled into `g`, and the CAT_MAP block wrote
 * `cd` onto real income-statement keys.
 *
 * The standing §12 KEEP-note called them "shape-load-bearing, value-inert". The first half
 * was true. ★ THE SECOND HALF WAS MEASURED FALSE ON 2026-09-03: when a tenant has an ORPHAN
 * employee (a department with no `department_mappings` row) whose department NAME HAPPENS TO
 * EQUAL one of the six, AND that is_category has no surviving display group, the orphan landed
 * in a real `dd` bucket, rolled into `cd`, and the CAT_MAP block BOOKED IT ON SEMITEK'S CHART.
 * Forged on cid=3's real roster: **$343,687.29 into cogs_\*** (drop the COGS mapping, move its
 * 5 employees to "Customer Success") and **$1,591,698.98 into rd_\*** (drop R&D's, move its 13
 * to "Engineering").
 *
 * ★★ AND THE REASON NO SNAPSHOT EVER SAW IT: in that arm the income statement's forecast
 * TOTAL is IDENTICAL to the unperturbed control — **Δ$0.00 against the golden**. The money is
 * not lost, it is silently RE-BOOKED, so nothing that compares totals can detect it. Only a
 * per-key comparison against a non-colliding control (the same orphans renamed to a
 * non-Semitek string) shows the difference.
 *
 * ⇒ REMOVED 2026-09-04. Acceptance: every serialized cell that disappeared was proved ZERO
 *   on cid 3/8/36/40 first (4 `cd` keys, 6 `dd` keys, and 324 historical month-entries inside
 *   12 surviving `isLines` keys — 8,568 cells per standing golden, 0 non-zero), and a
 *   shape-blind PRE/POST diff moved **$0.00** at the payroll layer AND through the full
 *   runFullModel sweep, on all four tenants. Full record: diffs/semitek-removal-phase0.txt
 *   and diffs/semitek-removal-build.txt.
 *
 * ⛔ DO NOT REINTRODUCE A CROSS-TENANT DEPARTMENT OR CATEGORY LIST HERE. A tenant's
 *   departments come from its own `department_mappings` and from nowhere else; an employee
 *   outside them is REPORTED (orphanDeptWarnings, below), never routed on a guess. */

/* ⛔ REMOVED with the constants above — `CAT_TO_IS_FALLBACK`, `ROLE_TO_FIELD` and
 * `buildCatToIs`. The fallback was Semitek's own is_line_key spellings
 * (`sm_compensation`, `*_payroll_tax`) returned whenever a caller passed no chart of
 * accounts. Its `if (!isLines) return CAT_TO_IS_FALLBACK` arm WAS live code, reachable
 * only through `buildCatToIs` → `CAT_MAP` → the emission block; deleting that block
 * makes the whole chain genuinely dead, which is why all four go together and not
 * one at a time. */

/* ⛔ REMOVED — see the note above CAT_TO_IS_FALLBACK's removal. The block below is
 * the retired code, kept only as the record of what it did:
 *
 * const ROLE_TO_FIELD = {
 *   payroll_wages: 'wages', payroll_bonuses: 'bonuses', payroll_commissions: 'commissions',
 *   payroll_tax: 'tax', payroll_benefits: 'benefits',
 * };
 *
 * buildCatToIs(isLines) walked the chart, bucketed each payroll-roled leaf by its id
 * PREFIX into one of the four Semitek category ids, and recorded `null` where a category
 * had two or more candidates ("SKIP, DON'T CHOOSE" — a multi-display-group category has
 * no single correct destination). Its output, CAT_MAP, was the write target of the
 * category-level emission block, which is what the removal deletes. */

/* ⛔ REMOVED (buildCatToIs). Its "SKIP, DON'T CHOOSE" note is preserved here because the
 * reasoning outlives the function: CAT_MAP was a WRITE TARGET for ONE category-level total,
 * not a read set, so broadcasting that total to all N keys of a multi-display-group category
 * MULTIPLIED it (measured 4x on cid=3's four-group S&M). That is the OPPOSITE fix from its
 * display-side twin (ScenariosPage deptCfg / IS_AGG_KEYS), which needed the full key SET
 * precisely because it SUMS from them. ⛔ If anything here is ever revived, do not "accumulate".
 *
 * ⓘ The last-wins collapse this note describes is now MOOT for the engine: with the emission
 *   block gone there is no category-level write target left to collapse. */

const FREQ_DIVISOR = { monthly: 12, quarterly: 4, semi_annual: 2, annual: 1 };
// FREQ_MONTHS is imported from utils/compCycle (B1) — identical values, one home.

// Compute bonus payout months for a given frequency and start month within a year
function bonusPayoutMonths(frequency, startMonth, year) {
  if (!frequency || !FREQ_DIVISOR[frequency]) return [];
  const interval = FREQ_MONTHS[frequency];
  const [sY, sM] = (startMonth || `${year}-01`).split("-").map(Number);
  const months = [];
  // Find first payout in this year based on cycling from start month
  let m = sM;
  // If start year is before this year, find the cycle position
  if (sY < year) {
    const monthsElapsed = (year - sY) * 12;
    const remainder = monthsElapsed % interval;
    m = remainder === 0 ? sM : sM + (interval - remainder);
    if (m > 12) m = m - 12; // shouldn't happen with correct math but safety
  } else if (sY > year) {
    return []; // start year is in the future
  }
  while (m <= 12) {
    months.push(m);
    m += interval;
  }
  return months;
}

/* ★ SEMITEK SWEEP BATCH 1 (finding A1) — a MISSING payroll-cycle anchor FAILS LOUD.

   `bonus_start_month` and `commission_start_month` used to fall back to the literal
   "2026-01" — SEMITEK'S first forecast month, not a neutral default. On a tenant
   whose window does not contain 2026 (cid=3 runs 2021-01..2023-12) that anchor sits
   OUTSIDE `MONTHS` entirely, so `MONTHS.includes(payKey)` never matched and the
   bonus/commission SILENTLY booked $0 for every month, forever — no warning, no
   zero row, just money that is not there.

   ★ THE ANCHOR IS NOT DERIVABLE, AND THAT IS THE WHOLE POINT (operator ruling).
   A bonus or commission CYCLE IS COMPANY-WIDE — "the quarter closes in March/June/
   September/December" is a property of the COMPANY's compensation calendar, not of
   any one person. So the employee's own hire month is NOT a valid substitute (an
   earlier draft of this build derived it; that was wrong for exactly this reason),
   and neither is the model window's first month — both would fabricate a company
   policy out of an unrelated fact and book money into invented months.

   THEREFORE: when a cycle needs an anchor and none is stored, the engine
     (1) books $0 for that employee's bonus / commission — no fabricated months,
     (2) does NOT throw — the rest of the model computes normally,
     (3) RECORDS the gap in `missingAnchorWarnings` on the payroll result, so the
         omission is inspectable rather than silent.
   This mirrors the SUTA fail-loud backstop above it: never invent a value; book $0
   (visibly wrong) rather than a plausible number (invisibly wrong).

   Record shape matches the SUTA precedent so a future consumer can branch on
   `reason` the same way:
       { reason: 'missing_bonus_anchor' | 'missing_commission_anchor',
         employeeId, name, column, frequency }

   ★ NO OPERATOR-FACING UI HERE. Surfacing this to the operator (a pre-flight check
   naming the employees) is item-5 work — the pre-flight SELF-SET-DIFF pass — and is
   deliberately out of this batch. Today the flag is inspectable on
   payrollResult.missingAnchorWarnings and nothing renders it. ★ Per the catalog's
   own 7th "checks" lesson — A WARNING WITH NO READER IS NOT A CHECK — this is a
   half-measure ON PURPOSE, and it is not done until item 5 gives it a reader. */
/* * A3 - THE ONE PAYROLL WALK.
 *
 * Three things used to exist twice: the employee x month ITERATION over `ed`, the
 * PARTICIPATION GATE that decides which employee-months count, and the DEPARTMENT ->
 * is_category RESOLUTION. computePayroll did them inline while building `ed`;
 * PayrollPage did them again in its own `modern` useMemo, its comments pointing at engine
 * line numbers that had already rotted. They agreed - measured, 144 cells, 0 mismatches -
 * but nothing held them together.
 *
 * This owns all three. Callers supply only a callback and accumulate whatever projection
 * they need: the engine builds cdByDg (4 fields, per DISPLAY GROUP, for the IS leaves) and
 * dd (10 fields, per department, legacy path); the page builds its 10-field per-DEPARTMENT
 * view. One walk, one gate, one resolution - two projections.
 *
 * * ITERATION ORDER IS LOAD-BEARING, NOT INCIDENTAL. Every accumulation in both callers is
 * `r2(acc + v)` - rounded at each step - so the ORDER in which slots arrive changes the
 * cents. This iterates employees-outer, months-inner, exactly the order the engine's
 * original in-loop aggregation ran in. Do not "optimise" it into a different traversal.
 *
 * * THE GATE is the engine's, verbatim: `!slot.on && slot.commission === 0`. The page's
 * copy read `(slot.commission || 0) === 0`. Those differ only for a non-numeric
 * commission - measured across all 2,232 cid=3 ed slots, every field is a finite number
 * and `on` is boolean, so the two forms are equivalent on real data. The engine's is
 * adopted because the engine books the money.
 *
 * `deptMappings` empty (legacy Semitek / Chris Test) => every slot resolves to
 * isCategory=null and the caller decides - which is exactly the old `else` branch.
 */
export function forEachPayrollSlot(ed, employees, months, deptMappings, cb) {
  // dept name -> { catKey, displayGroup }. Built once; skips any department whose
  // is_category has no slug ('Other'/unmapped), exactly as both callers did.
  const resolver = {};
  for (const dm of (deptMappings || [])) {
    const catKey = IS_CATEGORY_DISPLAY_TO_SLUG[dm.is_category];
    if (!catKey) continue;
    resolver[dm.department_name] = { catKey, displayGroup: dm.display_group || catKey };
  }
  for (const emp of (employees || [])) {
    const em = ed && ed[emp.id];
    if (!em) continue;
    const ctx = resolver[emp.department] || null;
    for (const m of (months || [])) {
      const slot = em[m];
      if (!slot) continue;
      /* THE GATE. Step 3 widens it to admit a severance- or COBRA-bearing slot. WITHOUT
         THIS, a severance paid in a month with no salary and no commission is `continue`d
         here and NEVER REACHES THE INCOME STATEMENT -- silently, with the balance sheet
         still balancing. That is the whole difficulty of this step, and it is forged in
         the build record as a NAIVE arm showing the charge vanish. */
      /* Commission ship 4 widens it once more, the same way: an accruing company's commission EXPENSED in a month with no salary
         and no payment (a leaver's cycle ends after the last day; 'period_end' expenses at the cycle's last month) must reach the
         income statement. commissionExpense exists only for an accruing company, so every other slot gates exactly as before; the
         PAID commission still counts here as it always did. */
      if (!slot.on && slot.commission === 0 && !slot.severance && !slot.cobra && !slot.commissionExpense) continue;   // <- THE gate
      cb({
        employee: emp, month: m, slot,
        isCategory: ctx ? ctx.catKey : null,
        displayGroup: ctx ? ctx.displayGroup : null,
        on: slot.on,
      });
    }
  }
}

export function computePayroll(emps, taxCfg, commissionData, commissionDelayMonths, allMonths, cutoff, isLineDefs, deptMappings, compCycleBasis, bonusPayoutDelayMonths, paySchedule, commissionLineKeys, commissionAccrual) {
  const MONTHS = allMonths || [];
  /* BI-WEEKLY PAYROLL, STEP 3b (2026-10-08) -- THE ACCRUED-PAYROLL BALANCE. `paySchedule` is the 11th, optional argument
     (engines/paySchedule.js payScheduleFromSettings). ONLY a bi-weekly company that ACCRUES payroll carries the balance:
     with no schedule, a never-answered one, semi-monthly, or bi-weekly expensed when paid, `accrualOn` is false, NOTHING
     below that reads it runs, and the result gains no key (the five tenants: a whole-result diff shows 0 added leaves).
       accrued WAGES  per employee, at each month-end: accruedSalaryByMonth -- earned through the month-end on the per-day
                      ledger less the checks dated on or before it, SIGNED (negative = a prepaid day). From the roster's
                      own DATES: emp.salary, emp.startDate, emp.end_date (the REAL last day -- NOT emp.termDate, which
                      ModelProvider infers as "<last paid month>-28" for month gating), emp.raises.
       accrued TAX    only when the accrual covers 'wages_and_taxes' (Brian B8). Brian Q2 + A2, PROPORTIONAL:
                        r2(the month's total employer tax x accrued wages / the month's TOTAL taxable pay)
                      taxable pay = totalComp (salary + bonus + commission + severance); 0 when it is 0; it follows the
                      sign of the accrued wages. The tax EXPENSE lines are untouched -- this only says how much of the
                      month's tax is still owed at the month-end.
     Bonuses, commissions and severance never enter the accrued WAGES (Brian B7): none is an input of the derivation. They
     reach the accrued tax only through the month's average rate.
     A SCHEDULE THAT CANNOT MAKE A CALENDAR MUST NOT CRASH THE MODEL: the derivation throws a named error for an impossible
     schedule or an impossible date. It is caught HERE -- the one place this engine calls the calendar -- recorded on the
     result as payScheduleError ({ message, employees: [{ id, name, message }] }), and that employee (or, for a schedule
     problem, every employee) carries no accrued balance. The pre-flight's P21 names it (BLOCKING). */
  const accrualOn = accruesPayroll(paySchedule);
  const accrualTaxOn = accrualCoversTax(paySchedule);
  const accruedWagesTotal = {};
  const accruedTaxTotal = {};
  let payScheduleError = null;
  let noPayCalendar = false;   // the SCHEDULE itself cannot make a calendar (as opposed to one employee's dates)
  if (accrualOn) {
    MONTHS.forEach(m => { accruedWagesTotal[m] = 0; accruedTaxTotal[m] = 0; });
    const problem = payCalendarProblem(paySchedule);
    if (problem) {
      noPayCalendar = true;
      payScheduleError = { message: problem, employees: [] };
      console.warn('[computePayroll] The pay schedule cannot make a calendar (' + problem + ') -- no accrued payroll is computed.');
    }
  }
  // ★ B3a (A3, dead site): was `|| "2025-12"` — Semitek's window edge. Not reached (the
  // caller always passes a value, verified in diffs/sweep-b3-verify.txt step 0); the
  // literal is removed so a genuinely-null date propagates honestly.
  const CUTOFF = cutoff || null;
  /* COMMISSION SHIP 4 (2026-10-09) -- THE ACCRUED-COMMISSIONS BALANCE. `commissionAccrual` is the 13th, optional argument
     (engines/commissionAccrual.js commissionAccrualFromSettings). ONLY a company that ACCRUES commissions runs anything below that
     reads commAccrualOn; every other company (never asked, 'when_paid', or the argument absent) computes exactly as before and the
     result gains no key.
       EXPENSED  Brian B1: basis 'monthly' (or NULL) -> each month's earned commission in the month earned, whatever the cycle;
                 'period_end' -> the cycle's whole earned sum in the cycle's last month. A closed month is the GL's: nothing is
                 expensed on or before the close.
       PAID      unchanged: the cycle's sum at cycle close + the lag (commPayouts, the slot's commission). The employer tax,
                 totalComp, the headcount and the slot gate keep reading the PAID commission (C5).
       THE CLOSE R9 + B1: earned commission EXPENSED on or before the close (by the basis) and PAID after it -- the computed close
                 balance. Each part is paid in its own payout month (it is already in commPayouts).
       BALANCE   the close figure at the close month; at each forecast month-end the close + the expensed - the paid since.
     K5: the income-statement line (folded Bonuses, or the group's own Commissions line) carries the EXPENSED amount for an
     accruing company and the PAID amount otherwise. */
  const commAccrualOn = accruesCommissions(commissionAccrual);
  const commBasis = commissionAccrualBasis(commissionAccrual);
  const isClosedMonth = m => !!(CUTOFF && m <= CUTOFF);
  const commExpensedTotal = {}, commPaidTotal = {};
  let commCloseTotal = 0;
  if (commAccrualOn) MONTHS.forEach(m => { commExpensedTotal[m] = 0; commPaidTotal[m] = 0; });

  // ★ COMP-CYCLE ENGINE CORE (item #1) — DERIVE a null bonus/commission anchor from the
  // COMPANY cycle instead of failing loud. This is the B1 header's own resolution: "a
  // bonus/commission cycle is company-wide" — so we now carry that company policy
  // (compCycleBasis) and derive the anchor from it, rather than fabricating from a
  // per-person fact. An EXPLICIT per-employee anchor still WINS (derive only fires on null).
  // The fail-loud below stays as the belt-and-suspenders for a null anchor AND no/unknown
  // basis. `compCycleBasis` is the calendar-ALIGNMENT gate ('calendar_period_end' today);
  // the BONUS payout OFFSET is a separate 0/1/2 numeric (bonusPayoutDelayMonths), PARALLEL to
  // commissionDelayMonths — a number, not more enum values, so future fiscal alignments don't
  // combinatorially explode with the offset.
  //   · BONUS anchor = the calendar period-END for the frequency, in the close year, PLUS
  //       bonusPayoutDelayMonths (year rollover): quarterly YYYY-03 (+2→YYYY-05); annual YYYY-12
  //       (+1→(YYYY+1)-01, +2→(YYYY+1)-02); monthly→YYYY-01 (offset irrelevant — pays every month).
  //   · COMMISSION anchor = the period-START. SINCE 2026-10-09 (commission ship 1, Brian Q7): January of the window's
  //       first year, so derived cycles are CALENDAR periods (utils/compCycle.js deriveCommAnchor). It previously read
  //       "the first forecast month (close month + 1)". The bonus offset NEVER touches it -- commissions use
  //       commissionDelayMonths at cycle close.
  // ★ B1 — the two local closures that stood here are DELETED; both derivations are imported
  // from utils/compCycle. They took (freq) / () and CAPTURED compBasis / closeYear / bonusDelay /
  // firstForecastMonth from this scope; the shared ones take everything explicitly, so the call
  // sites below pass CUTOFF (from which compCycle derives the close YEAR exactly as `closeYear`
  // did — both do Number(split('-')[0])) and MONTHS (from which it finds the first forecast month
  // exactly as `firstForecastMonth` did). `closeYear` and `firstForecastMonth` had no other reader
  // and are gone with them.
  const compBasis = compCycleBasis || null;
  const bonusDelay = Number.isFinite(bonusPayoutDelayMonths) ? bonusPayoutDelayMonths : (parseInt(bonusPayoutDelayMonths, 10) || 0);

  // Session 3b: deptToContext lookup drives per-display_group aggregation for
  // tenants with department_mappings populated. ★ It is now the ONLY department
  // resolver in this engine — the Semitek hardcode it used to fall through to was
  // removed 2026-09-04 (see the note at the top of this file). A department that does
  // not resolve here is REPORTED, never routed.
  const deptToContext = {};
  for (const dm of (deptMappings || [])) {
    const catKey = IS_CATEGORY_DISPLAY_TO_SLUG[dm.is_category];
    if (!catKey) continue;
    deptToContext[dm.department_name] = {
      catKey,
      displayGroup: dm.display_group || catKey,
    };
  }

  // ★ TWO TENANT MODES, DECIDED BY THE DATA. (There were THREE until 2026-09-04; the
  // LEGACY mode — the Semitek taxonomy, gated on `useLegacyDeptFallback` — went with the
  // constants it read, having been false on every live tenant with no writer since the
  // cid=1/cid=2 teardown.)
  //   MAPPED  (department_mappings present): the cdByDg path.
  //   UNMAPPED(NO mappings): FAIL LOUD. Every employee aggregates into a single explicit
  //           'unmapped' department, and `missingDeptMappings` records the gap.
  //
  // ★ WHAT 'UNMAPPED' MEANS FOR THE MONEY. In UNMAPPED mode the payroll dollars stay
  // visible in `dd` and in `g` (so the Payroll page and the company total still show them)
  // but they reach NO is_category, because without a department->category mapping there is
  // no honest way to know which one. The IS payroll lines therefore book $0 — visibly wrong
  // — rather than being routed on the authority of another company's chart. That is the
  // same "book $0 (visibly wrong) over a plausible number (invisibly wrong)" rule the SUTA
  // and missing-anchor backstops follow.
  // ⓘ `g` is now fed ONLY by the explicit unmapped fold further down; on a MAPPED tenant
  //   its money fields are $0, which is exactly what they were before the removal (the roll
  //   that used to feed them summed the all-zero Semitek buckets). Measured PRE vs POST on
  //   cid 3/8/36/40: 0 differing cells.
  //
  // ★★ THE *MIXED* MODE — a tenant that HAS mappings but has an employee outside them
  // (orphanDeptWarnings, below) — USED TO BE THE DANGEROUS ONE, AND THIS IS THE RECORD OF
  // WHY, BECAUSE THE FIX IS INVISIBLE IF YOU ONLY READ THE CODE THAT REMAINS:
  //
  //   (i) THE DOLLARS DO NOT STAY VISIBLE IN dd/g IN MIXED MODE. On a dept-mapped tenant
  //       the money flows through cdByDg. Measured on cid=3 UNPERTURBED: dd = $0 and g = $0
  //       for the forecast window. An orphan is visible ONLY in `ed` and in its own warning.
  //       That is still true today and is why the warning exists.
  //
  //  (ii) ⛔ AND UNTIL 2026-09-04 THE MONEY COULD BE ROUTED ON ANOTHER COMPANY'S CHART.
  //       DD_KEYS seeded the six Semitek department names for every tenant, so an orphan
  //       whose department NAME collided with one of them landed in a real `dd` bucket,
  //       rolled into `cd`, and was written to the income statement by the CAT_MAP block.
  //       It was harmless whenever the cdByDg block later overwrote those forecast cells —
  //       but when that is_category had NO display group, nothing overwrote it.
  //       MEASURED on cid=3's real roster: **$343,687.29 into cogs_*** with COGS unmapped,
  //       and **$1,591,698.98 into rd_*** with R&D unmapped — both at **Δ$0.00 against the
  //       golden**, because the TOTAL does not move. No snapshot could see it.
  //       ⇒ CLOSED by removing the constants (top of this file). The same forged arms now
  //         book $0.00 onto those keys and match their non-colliding controls exactly, so
  //         the money is VISIBLY absent instead of INVISIBLY re-booked.
  //
  // ⇒ THE ORPHAN WARNING STILL STATES THE AMOUNT AND NOT THE DESTINATION, and that stays
  //   deliberate: this engine no longer books an orphan anywhere, but the warning's job is
  //   to name what is missing, not to assert where it went. ⛔ Do not "improve" it into a
  //   destination claim.
  /* ★ P4-b — `legacyDeptMode` is gone with the `useLegacyDeptFallback` param. It was always
   * false (the flag is false on all four tenants, with no writer), so the guard it added to
   * `unmappedDeptMode` was always satisfied and the expression collapses. */
  const unmappedDeptMode = Object.keys(deptToContext).length === 0;
  const UNMAPPED_DEPT = 'unmapped';
  // Sibling of sutaWarnings / missingAnchorWarnings — ONE warnings pattern, not three.
  const missingDeptMappings = [];

  // Per-(catKey, displayGroup) aggregation parallel to existing cd[catId][m].
  // Commissions FOLD INTO bonuses at aggregation time per operator clarification —
  // no separate commissions slot. Populated only for employees resolved via
  // deptToContext. An employee who does NOT resolve there aggregates into `dd` under their
  // own department string and is recorded in orphanDeptWarnings — it reaches no is_category.
  const cdByDg = {};
  /* Commission ship 2c (R14): the (category, group) pairs with their OWN Commissions line -- the *_commissions keys of
     utils/perDgPayrollActuals commissionLineKeys (the one qualifying set; buildISLines emits the same leaves). Absent/empty ->
     every group folds commission into bonuses, exactly as before. The employer tax stays on the group's Payroll Taxes line. */
  const COMMISSION_LINE_KEYS = new Set(commissionLineKeys || []);
  const YEARS = Array.from(new Set(MONTHS.map(m => parseInt(m.split("-")[0], 10)))).sort((a, b) => a - b);
  const delay = commissionDelayMonths || 0;
  const ed={}, dd={}, cd={}, g={};
  // Fail-loud SUTA backstop (multi-state build 2a): three distinct failure modes
  // (no_state_row / no_default_rate / rate_year_fallback) each record a structured
  // entry here and compute SUTA = $0. We NEVER substitute an invented rate —
  // booking $0 is visibly wrong; a made-up rate is invisibly wrong. Surfaces as a
  // Model-Health flag via the result.sutaWarnings.
  const sutaWarnings = [];
  /* ★ PART A2 — THE FIFTH MEMBER OF THE WARNINGS FAMILY, and it is a SEPARATE ARRAY for
     the same reason missingAnchorWarnings is: PayrollPage's Model-Health panel branches on
     `sutaWarnings`' own `reason` values and must not see foreign ones. Same RECORD SHAPE as
     SUTA's year fallback ({ reason, month, yearUsed }) — "ONE warnings pattern", one array
     per domain. Fires when a month's calendar year has no federal_tax_rates row and the
     most recent published year is used instead. */
  const fedTaxWarnings = [];
  const warnedFedYearFallback = new Set();
  /* The published federal years, ascending. Read once per call, not per month. ⛔ Returns
     [] when the table is absent or empty, which is what routes the resolve above back to
     the statutory-literal buckets. */
  const FED_YEARS = (cfg) => {
    const m = cfg && cfg.fedByYear;
    return m ? Object.keys(m).map(Number).filter(n => !Number.isNaN(n)).sort((a, b) => a - b) : [];
  };
  // ★ A1 fail-loud: employees whose bonus/commission cycle needs an anchor month
  // and has none. Sibling of sutaWarnings (same record shape, same never-invent
  // policy); kept SEPARATE because PayrollPage's Model-Health panel branches on
  // sutaWarnings' own `reason` values and must not see foreign ones.
  const missingAnchorWarnings = [];
  /* ★ THE MIXED-TENANT RECORD. Fourth member of the warnings family (sutaWarnings /
   * missingAnchorWarnings / missingDeptMappings), and the PER-EMPLOYEE shape — the
   * anchor-warning shape, not the tenant-level dept one.
   *
   * WHAT IT CATCHES: an employee whose department does not resolve to an is_category
   * on a tenant that HAS department_mappings. Their payroll reaches no IS category and
   * is not in the P&L — measured at -$87,117 for ONE employee on cid=3. Reachable four
   * ways today, the widest being a BLANK Department cell in the roster sheet: the
   * parser lists departments only for non-blank cells, so Step 5c never shows it, the
   * all-mapped gate is satisfied, and the import stores NULL.
   *
   * ⛔ IT IS NOT A THROW. A throw here blanks IS/BS/CFS/KPI for the whole tenant over
   * one empty spreadsheet cell. The money is still dropped (booking it to an invented
   * bucket would route payroll no operator chose — the same refusal the UNMAPPED note
   * above states); what changes is that it is now SAID.
   *
   * ⓘ MUTUALLY EXCLUSIVE WITH missingDeptMappings BY CONSTRUCTION: that one fires only
   * when `unmappedDeptMode`, this one only when NOT. The two can never double-report. */
  const orphanDeptWarnings = [];
  /* Step 3. Rides payrollResult exactly as orphanDeptWarnings does. A READER is owed:
     see the build record -- a warning nobody renders is not a warning (rule 42). */
  const severanceWarnings = [];
  const warnedOrphanEmps = new Set();   // dedupe: the walk below runs per employee-MONTH
  const warnedSutaEmps = new Set();     // dedupe employee-level warnings (per emp id)
  const warnedYearFallback = new Set(); // dedupe year-fallback warnings (per absent year)
  // Per-year SUTA map. taxCfg.sutaByYear = { <year>: { <ST>: { rate, cap, isOverride } } }.
  // Back-compat: if only the flat taxCfg.suta is present (e.g. runFullModel's fallback
  // config), treat it as applying to every year.
  const sutaByYear = taxCfg.sutaByYear || null;
  const flatSuta = taxCfg.suta || {};
  const sutaYears = sutaByYear ? Object.keys(sutaByYear).map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b) : [];
  const latestSutaYear = sutaYears.length ? sutaYears[sutaYears.length - 1] : null;

  // ★ payroll-hc fix (a): dd carries the TENANT'S OWN department names.
  // runFullModel reads `prResult.headcount` keyed on department_mappings.department_name,
  // so on a dept-mapping tenant a `dd` seeded from anything else hit an absent key and the
  // `?.` silently yielded 0 — payrollHC was empty for the whole window and per-seat vendors
  // (its only consumer) computed $0.
  //
  // ⛔ THIS USED TO BE A UNION WITH SEMITEK'S SIX DEPARTMENT NAMES, seeded unconditionally
  //   for every tenant. Those six keys were all-zero on every live tenant — and they were
  //   ALSO the vehicle for the mis-routing described above (ii), because an orphan whose
  //   name matched one of them found a real bucket waiting. Removed 2026-09-04. `dd` is now
  //   exactly the tenant's own departments, plus the single 'unmapped' bucket in UNMAPPED
  //   mode. ⛔ Do not re-seed it with names the tenant did not supply.
  const DD_KEYS = [...new Set([...Object.keys(deptToContext), ...(unmappedDeptMode ? [UNMAPPED_DEPT] : [])])];
  DD_KEYS.forEach(d=>{dd[d]={};MONTHS.forEach(m=>{dd[d][m]={s:0,b:0,c:0,t:0,ss:0,med:0,futa:0,suta:0,ben:0,tot:0,hc:0};});});
  // ⓘ `cd` is DELIBERATELY LEFT EMPTY and still returned (see the return statement). It was
  //   created here from the four Semitek category ids; it is kept as a key so consumers need
  //   no existence check (PayrollPage reads `payrollData?.cd || {}`) and so no capture loses
  //   a top-level key.
  MONTHS.forEach(m=>{g[m]={s:0,b:0,c:0,t:0,ss:0,med:0,futa:0,suta:0,ben:0,tot:0,hc:0};});

  emps.forEach(emp=>{
    ed[emp.id]={};
    let ytd = { wages: 0, ss: 0, futa: 0, suta: 0 };
    let prevYear = null;

    // Pre-compute bonus payout months per year
    const bonusAmt = parseFloat(emp.bonus_amount || emp.bonusAmount || 0);
    const bonusFreq = emp.bonus_frequency || emp.bonusFrequency || null;
    // ★ COMP-CYCLE: explicit anchor wins; else DERIVE the calendar period-END anchor
    // from the company cycle (null only when the basis is absent/unknown → fail-loud below).
    const bonusStart = emp.bonus_start_month || emp.bonusStartMonth || deriveBonusAnchor(compBasis, CUTOFF, bonusFreq, bonusDelay) || null;
    const payoutPerPeriod = bonusFreq && bonusAmt > 0 ? r2(bonusAmt / (FREQ_DIVISOR[bonusFreq] || 1)) : 0;
    const bonusPayouts = {}; // { "2026-03": payoutAmount, ... }
    // ★ FAIL LOUD: a real bonus (amount + frequency) with no anchor books $0 and is
    // recorded. bonusPayouts stays EMPTY — no month is fabricated.
    if (payoutPerPeriod > 0 && !bonusStart) {
      missingAnchorWarnings.push({
        reason: 'missing_bonus_anchor', employeeId: emp.id, name: emp.name || null,
        column: 'bonus_start_month', frequency: bonusFreq,
      });
      console.warn(`[computePayroll] Employee ${emp.id} ${emp.name || ''} has a ${bonusFreq} bonus but no bonus_start_month — booking $0. A bonus cycle is company-wide and cannot be inferred; set the anchor month.`);
    } else if (payoutPerPeriod > 0) {
      YEARS.forEach(year => {
        const payMonths = bonusPayoutMonths(bonusFreq, bonusStart, year);
        payMonths.forEach(mo => {
          const mKey = `${year}-${String(mo).padStart(2, "0")}`;
          bonusPayouts[mKey] = payoutPerPeriod;
        });
      });
    }

    // Pre-compute commission payouts — cycle-close logic
    // Anchor defines first month of cycle; payout at cycle CLOSE (last month)
    const commFreq = emp.commission_frequency || "monthly";
    // ★ COMP-CYCLE: explicit anchor wins; else DERIVE the period-START anchor (since 10-09: January of the window's
    // first year -- calendar periods) from the company cycle (null only when basis absent → fail-loud below).
    const commStart = emp.commission_start_month || deriveCommAnchor(compBasis, MONTHS, CUTOFF) || null;
    const empCommData = commissionData?.[emp.id] || {};
    const commPayouts = {}; // { "2026-04": amount }
    const commExpense = {};   // commission ship 4, accrues only: { month: commission EXPENSED } (B1)
    let commClose = 0;        // commission ship 4, accrues only: this rep's part of the computed close balance (R9 + B1)
    if (Object.keys(empCommData).length > 0) {
      const interval = FREQ_MONTHS[commFreq] || 1;
      if (interval === 1) {
        // Monthly: every month is a payout, just apply delay
        MONTHS.forEach(m => {
          const raw = empCommData[m] || 0;
          if (raw > 0) {
            const [mY, mM] = m.split("-").map(Number);
            let payY = mY, payM = mM + delay;
            while (payM > 12) { payM -= 12; payY += 1; }
            const payKey = `${payY}-${String(payM).padStart(2,"0")}`;
            // Commission ship 1 (R12, 2026-10-09): commissionData now covers CLOSED months too; a payout dated on or before
            // the close is NOT booked -- the GL owns closed months. One dated after it is booked in its payout month.
            if (MONTHS.includes(payKey) && !(CUTOFF && payKey <= CUTOFF)) {
              commPayouts[payKey] = r2((commPayouts[payKey] || 0) + raw);
            }
            /* Commission ship 4 (accrues only): a monthly cycle IS its month -- expensed in the month earned under either basis; a
               closed month's commission still owed at the close (paid after it) is part of the computed close balance. */
            if (commAccrualOn) {
              if (!isClosedMonth(m)) commExpense[m] = r2((commExpense[m] || 0) + raw);
              else if (!isClosedMonth(payKey)) commClose = r2(commClose + raw);
            }
          }
        });
      } else if (!commStart) {
        // ★ FAIL LOUD: a non-monthly commission cycle NEEDS an anchor to know where
        // the cycle closes, and it is a company-wide policy — not inferable. Book $0
        // (commPayouts stays empty; no month fabricated) and record the gap.
        // NOTE monthly commissions are unaffected: the interval===1 branch above
        // pays in the earning month and never reads the anchor, so no warning fires
        // for them (a check that cries wolf is a check nobody reads).
        missingAnchorWarnings.push({
          reason: 'missing_commission_anchor', employeeId: emp.id, name: emp.name || null,
          column: 'commission_start_month', frequency: commFreq,
        });
        console.warn(`[computePayroll] Employee ${emp.id} ${emp.name || ''} has ${commFreq} commissions but no commission_start_month — booking $0. A commission cycle is company-wide and cannot be inferred; set the anchor month.`);
      } else {
        // Quarterly/semi_annual/annual: anchor-based cycle-close payout
        const [aY, aM] = commStart.split("-").map(Number);
        const anchorAbs = aY * 12 + (aM - 1); // absolute month index of anchor
        // Build payout months: last month of each cycle (anchor + interval - 1, anchor + 2*interval - 1, ...)
        // For each month in MONTHS, determine which cycle it belongs to and its close month
        const payoutSet = new Set();
        const cycleMap = {}; // payoutKey -> [list of months in that cycle]
        MONTHS.forEach(m => {
          const [mY, mM] = m.split("-").map(Number);
          const mAbs = mY * 12 + (mM - 1);
          const offset = mAbs - anchorAbs;
          if (offset < 0) return; // before anchor, skip
          const cycleIdx = Math.floor(offset / interval);
          const closeAbs = anchorAbs + (cycleIdx + 1) * interval - 1;
          const closeY = Math.floor(closeAbs / 12);
          const closeM = (closeAbs % 12) + 1;
          const closeKey = `${closeY}-${String(closeM).padStart(2,"0")}`;
          if (!cycleMap[closeKey]) cycleMap[closeKey] = [];
          cycleMap[closeKey].push(m);
          payoutSet.add(closeKey);
        });
        // For each cycle close, sum commissionData across cycle window, apply delay
        payoutSet.forEach(closeKey => {
          const windowMonths = cycleMap[closeKey] || [];
          const cycleSum = r2(windowMonths.reduce((s, m) => s + (empCommData[m] || 0), 0));
          if (cycleSum > 0) {
            const [cY, cM] = closeKey.split("-").map(Number);
            let payY = cY, payM = cM + delay;
            while (payM > 12) { payM -= 12; payY += 1; }
            const payKey = `${payY}-${String(payM).padStart(2,"0")}`;
            // R12 (as above): a cycle -- closed months included -- whose payout falls on or before the close is the GL's.
            if (MONTHS.includes(payKey) && !(CUTOFF && payKey <= CUTOFF)) {
              commPayouts[payKey] = r2((commPayouts[payKey] || 0) + cycleSum);
            }
            /* Commission ship 4 (accrues only), Brian B1. 'period_end': the whole cycle is expensed in its last month; a cycle that
               ended on or before the close and is paid after it is in the close balance. 'monthly': each earned month is expensed
               in its own month; a straddling cycle's closed months are already in the books -> the close balance. A cycle whose
               last month is past the window is expensed past it -- nothing is booked for it (as for its payment). */
            if (commAccrualOn) {
              if (commBasis === 'period_end') {
                if (!isClosedMonth(closeKey)) { if (MONTHS.includes(closeKey)) commExpense[closeKey] = r2((commExpense[closeKey] || 0) + cycleSum); }
                else if (!isClosedMonth(payKey)) commClose = r2(commClose + cycleSum);
              } else {
                windowMonths.forEach(wm => {
                  const amt = empCommData[wm] || 0;
                  if (!amt) return;
                  if (!isClosedMonth(wm)) commExpense[wm] = r2((commExpense[wm] || 0) + amt);
                  else if (!isClosedMonth(payKey)) commClose = r2(commClose + amt);
                });
              }
            }
          }
        });
      }
    }

    /* Commission ship 4 (accrues only): the company totals, employees-outer, r2 at each step like everything here. PAID is the
       booked payout (commPayouts, the slot's commission); EXPENSED is B1's. */
    if (commAccrualOn) {
      commCloseTotal = r2(commCloseTotal + commClose);
      MONTHS.forEach(m => {
        if (commExpense[m]) commExpensedTotal[m] = r2(commExpensedTotal[m] + commExpense[m]);
        if (commPayouts[m]) commPaidTotal[m] = r2(commPaidTotal[m] + r2(commPayouts[m]));
      });
    }

    /* SEVERANCE (step 3) -- resolved ONCE per employee, and booked in the TERMINATION
       MONTH only. The month comes from the employee's own termDate (ModelProvider infers
       it from monthlyData, falling back to end_date); a scenario sets the same field on its
       clone. An employee carrying a severance with NO termination month has no month to
       book it in -- that is NOT guessed (section E1). It is skipped and NAMED below.
       Unreachable from the UI, which will only offer severance inside a termination. */
    const sevPay = severanceFor(emp);
    const sevMonth = (emp.termDate || emp.end_date || "").slice(0, 7) || null;
    if (!sevMonth && (sevPay.severance > 0 || sevPay.cobra > 0)) {
        severanceWarnings.push({
            reason: 'severance_without_termination',
            employeeId: emp.id,
            name: emp.name || null,
            severance: sevPay.severance,
            cobra: sevPay.cobra,
        });
        console.warn('[computePayroll] ' + (emp.name || '#' + emp.id) +
            ' carries a severance but has no termination month -- NOT BOOKED.');
    }

    /* Bi-weekly + accrues only: this employee's accrued wages at every month-end, from the dates (see the head of this
       function). null = the company carries no accrued balance, or this employee's could not be derived (named). */
    let accruedByMonth = null;
    if (accrualOn && !noPayCalendar) {
      try {
        accruedByMonth = accruedSalaryByMonth(emp.salary, emp.startDate, emp.end_date ? String(emp.end_date).slice(0, 10) : null, emp.raises, MONTHS, paySchedule);
      } catch (e) {
        const message = (e && e.message) || String(e);
        if (!payScheduleError) payScheduleError = { message, employees: [] };
        payScheduleError.employees.push({ id: emp.id, name: emp.name || null, message });
        console.warn('[computePayroll] No accrued payroll for ' + (emp.name || '#' + emp.id) + ': ' + message);
      }
    }

    MONTHS.forEach(m=>{
      const [yr] = m.split("-");
      if (yr !== prevYear) { ytd = { wages: 0, ss: 0, futa: 0, suta: 0 }; prevYear = yr; }

      const sal = r2(emp.monthlyData[m]||0);
      /* `on` KEEPS ITS EXACT MEANING: ordinary salary was drawn this month. Step 3
         deliberately does NOT widen it. Three consumers want salary only -- the bonus gate
         just below, the benefits gate, and headcount (which reads slot.sal directly and so
         needs nothing) -- and widening `on` would fire all three on a severance.
         The two consumers that DO want severance are served by their own terms instead:
         totalComp below (so it is taxed and consumes the wage bases) and the shared
         iterator's gate, which now admits a severance- or COBRA-bearing slot. */
      const on = sal > 0;
      /* SEVERANCE and COBRA -- a ONE-TIME charge in the termination month. */
      const isSevMonth = sevMonth !== null && m === sevMonth;
      const severance = isSevMonth ? sevPay.severance : 0;
      const cobra = isSevMonth ? sevPay.cobra : 0;

      // Bonus: only paid if employee is active and month is a payout month
      const bonus = on && bonusPayouts[m] ? r2(bonusPayouts[m]) : 0;
      // Commission: paid even after termination (final cycle payout at cycle close)
      const commission = commPayouts[m] ? r2(commPayouts[m]) : 0;
      /* Total compensation for tax purposes = salary + bonus + commission + SEVERANCE.
         Severance is taxed as wages, so it belongs here, BEFORE the tax lines -- it is
         charged SS / Medicare / FUTA / SUTA and it CONSUMES the wage bases.
         COBRA IS ABSENT ON PURPOSE: it is a benefit, not compensation. */
      const totalComp = r2(sal + bonus + commission + severance);

      const isFc = m > CUTOFF;
      // ★ ONE year extraction, TWO consumers. `mYr` was declared below and used by SUTA
      // only; it is hoisted here so the federal resolve reuses it. ⛔ Do not add a third
      // (`yr` at the top of this loop is the YTD reset's string form).
      const mYr = parseInt(m.split("-")[0], 10);

      /* ★★ PART A2 — THE FEDERAL WAGE BASE IS RESOLVED BY THE MONTH'S CALENDAR YEAR.
         It used to be `isFc ? (cap2026 || cap) : cap` — selected by ACTUALS-vs-FORECAST,
         so a single calendar year was taxed against TWO bases either side of the close,
         and a 2027 row would have been IGNORED even if inserted. Silent: no warning.
         ⇒ THIS MIRRORS THE SUTA RESOLVE TWELVE LINES BELOW, deliberately and exactly:
           resolve by year · fall back to the most recent published year · push ONE record
           per absent year · dedupe via a Set · guard the warning with `isFc`.
         ⓘ ANNUAL vs MONTHLY, because it sets the priority: month by month the two bases
           differ, but ANNUALLY they did not — the forecast months re-opened headroom up to
           the LATER cap, so the year's total landed on cap2026 anyway. What this fixes for
           a tenant whose close-year base EQUALS cap2026 is WITHIN-YEAR TIMING (measured,
           $360k/yr, close 2026-07: 2026-06 1,618.20→1,860.00 · 07 0.00→279.00 ·
           08 520.80→0.00). The ANNUAL error bit a tenant whose close-year base is BELOW
           cap2026: 2025 overstated taxable by 8,400 (=$520.80 of SS per high earner).
         ⛔ FUTA IS FIXED THE SAME WAY AND MOVES NOTHING: ModelProvider sets futa.cap AND
           futa.cap2026 from the SAME fed2026 row, so the old ternary chose between two
           identical values. Corrected for consistency, not for a number.
         ⛔ MEDICARE IS UNTOUCHED AND STAYS UNCAPPED: `med` below is `totalComp * rate`,
           with no cap read at all. taxCfg.med.cap has ZERO readers anywhere (measured) and
           med.cap2026 is the literal null. ⇒ do NOT start applying a Medicare cap here.
         ⓘ EMPTY-TABLE PATH PRESERVED: with no fedByYear (or an empty one) this falls back
           to the pre-existing buckets, whose `|| 176100 / || 184500 / || 7000` statutory
           literals are the defence for an empty federal_tax_rates. Those literals stay. */
      const fedYears = FED_YEARS(taxCfg);
      const fedRow = fedYears.length
        ? (taxCfg.fedByYear[mYr] || taxCfg.fedByYear[fedYears[fedYears.length - 1]])
        : null;
      if (fedRow && !taxCfg.fedByYear[mYr] && isFc && !warnedFedYearFallback.has(mYr)) {
        warnedFedYearFallback.add(mYr);
        const usedYr = fedYears[fedYears.length - 1];
        fedTaxWarnings.push({ reason: 'fed_rate_year_fallback', month: m, yearUsed: usedYr });
        console.warn(`[computePayroll] No ${mYr} federal wage bases — using ${usedYr} (month ${m}).`);
      }
      const ssCap  = fedRow ? (parseFloat(fedRow.ss_cap)   || 0) : (isFc ? (taxCfg.ss.cap2026 || taxCfg.ss.cap) : taxCfg.ss.cap);
      const futaCap = fedRow ? (parseFloat(fedRow.futa_cap) || 0) : (isFc ? (taxCfg.futa.cap2026 || taxCfg.futa.cap) : taxCfg.futa.cap);
      // Resolve the SUTA map for THIS month's year (build 2a). A missing year uses
      // the latest available year AND fails loud on forecast months — never a silent
      // extrapolation. Historical months read is_actuals downstream (unaffected).
      let yearMap;
      if (sutaByYear) {
        yearMap = sutaByYear[mYr];
        if (!yearMap) {
          yearMap = latestSutaYear != null ? sutaByYear[latestSutaYear] : {};
          if (isFc && latestSutaYear != null && !warnedYearFallback.has(mYr)) {
            warnedYearFallback.add(mYr);
            sutaWarnings.push({ reason: 'rate_year_fallback', month: m, yearUsed: latestSutaYear });
            console.warn(`[computePayroll] No SUTA rates for year ${mYr} — using ${latestSutaYear} rates (month ${m}).`);
          }
        }
      } else {
        yearMap = flatSuta; // back-compat: flat map applies to every year
      }
      // Widened fail-loud guard — three conditions, each → SUTA $0 + one warning per
      // employee. A { rate:null } row is TRUTHY, so build-1's `if (!empSuta)` was
      // INSUFFICIENT (would silently compute taxable × null = 0 with no warning).
      const empSuta = yearMap ? yearMap[emp.state] : undefined;
      let sutaRate, sutaCap;
      if (!empSuta) {
        sutaRate = 0; sutaCap = null;
        if (!warnedSutaEmps.has(emp.id)) {
          warnedSutaEmps.add(emp.id);
          sutaWarnings.push({ reason: 'no_state_row', employeeId: emp.id, name: emp.name || null, state: emp.state || null });
          console.warn(`[computePayroll] No SUTA row for state ${JSON.stringify(emp.state)} (employee ${emp.id} ${emp.name || ''}) — booking SUTA $0. Add a state_tax_rates row or set the employee's state.`);
        }
      } else if (empSuta.rate == null) {
        sutaRate = 0; sutaCap = empSuta.cap != null ? empSuta.cap : null;
        if (!warnedSutaEmps.has(emp.id)) {
          warnedSutaEmps.add(emp.id);
          sutaWarnings.push({ reason: 'no_default_rate', employeeId: emp.id, name: emp.name || null, state: emp.state || null });
          console.warn(`[computePayroll] State ${JSON.stringify(emp.state)} has no default SUTA rate (employee ${emp.id} ${emp.name || ''}) — booking SUTA $0. Enter the company's experience rate.`);
        }
      } else {
        sutaRate = empSuta.rate;
        sutaCap = empSuta.cap;
      }

      // Tax on total comp (salary + bonus) with YTD caps
      const ssTaxable  = ssCap  ? r2(Math.max(0, Math.min(totalComp, ssCap  - ytd.ss)))  : totalComp;
      const futaTaxable = futaCap ? r2(Math.max(0, Math.min(totalComp, futaCap - ytd.futa))) : totalComp;
      const sutaTaxable = sutaCap ? r2(Math.max(0, Math.min(totalComp, sutaCap - ytd.suta))) : totalComp;

      const ss   = r2(ssTaxable  * taxCfg.ss.rate);
      const med  = r2(totalComp * taxCfg.med.rate);
      const futa = r2(futaTaxable * taxCfg.futa.rate);
      const suta = r2(sutaTaxable * sutaRate);
      const tax  = r2(ss + med + futa + suta);

      const benStartM = (emp.benefitsStartDate || emp.startDate || "").slice(0,7);
      const benOn = on && m >= benStartM;
      // ★ C-11 (shape A): benefits = Σ(benefit_components). The benOn gate is UNCHANGED
      // (benefitsStartDate || startDate — its own column; bc carries amounts only, no dates).
      /* COBRA RIDES THE BENEFITS LINE, UNTAXED. It is added AFTER the tax lines above, so
         no tax is computed on it and no wage base is consumed by it. The benOn gate is
         untouched -- benefits still stop at the termination month with the whole month
         paid (measured in step 1 Part A across five termination dates while salary ranged
         1,578.08 to 6,000.00), so that rule needed no change. */
      const ben  = r2((benOn ? sumBenefitComponents(emp.benefitComponents) : 0) + cobra);
      const tot  = r2(sal + bonus + commission + severance + tax + ben);

      /* THE WAGE-BASE ACCUMULATORS MUST SEE A SEVERANCE-ONLY MONTH. `on` alone would skip
         it, so a severance paid after the last salary month would be taxed without
         consuming any base -- and the next payment would be taxed again on a base the
         severance should already have used up. A commission-only month still does NOT
         accumulate, exactly as before: that asymmetry is pre-existing and untouched. */
      if (on || severance > 0) {
        ytd.wages += totalComp;
        ytd.ss   += ssTaxable;
        ytd.futa += futaTaxable;
        ytd.suta += sutaTaxable;
      }

      ed[emp.id][m] = { sal, bonus, commission, severance, cobra, tax, ss, med, futa, suta, ben, tot, on };
      if (commAccrualOn) ed[emp.id][m].commissionExpense = commExpense[m] ? r2(commExpense[m]) : 0;   // commission ship 4: the IS reads it (K5)
      /* THE ACCRUED BALANCE (bi-weekly + accrues only -- see the head of this function). AFTER the four tax lines, which
         it does not touch. Added to the slot and to the company totals only when the company accrues, so no other
         tenant's result gains a key. Summed with r2 at each step, employees-outer / months-inner like everything here. */
      if (accrualOn) {
        const accruedSalary = accruedByMonth ? (accruedByMonth[m] || 0) : 0;
        ed[emp.id][m].accruedSalary = accruedSalary;
        accruedWagesTotal[m] = r2(accruedWagesTotal[m] + accruedSalary);
        if (accrualTaxOn) {
          const accruedTax = totalComp ? r2((tax * accruedSalary) / totalComp) : 0;
          ed[emp.id][m].accruedTax = accruedTax;
          accruedTaxTotal[m] = r2(accruedTaxTotal[m] + accruedTax);
        }
      }
      // * A3 - the gate and the aggregation that stood here now run as a SECOND PASS over
      // the completed `ed`, via forEachPayrollSlot (just below this loop). Byte-identical:
      // the iterator walks employees-outer/months-inner, the same order this loop did, so
      // every r2()-at-each-step accumulation lands in the same sequence. This loop keeps
      // what only it can do - the YTD tax-cap state threaded across months.
    });
  });

  // * A3 - DUAL-PATH AGGREGATION, now driven by the ONE shared walk. Unchanged in what it
  // produces: the modern path fills cdByDg (commissions folded into bonuses), the legacy /
  // unmapped path fills dd. `isCategory === null` is precisely the old `!ctx`.
  forEachPayrollSlot(ed, emps, MONTHS, deptMappings, ({ employee, month: m, slot, isCategory, displayGroup }) => {
    /* COBRA is deliberately NOT destructured here: it is already inside `ben`, so a second
       term would double-count it. The lint set caught it as an unused binding -- which is
       exactly what the lint set is for (rule 51). */
    const { sal, bonus, commission, severance, tax, ss, med, futa, suta, ben, tot } = slot;
    if (isCategory) {
      const catBucket = cdByDg[isCategory] = cdByDg[isCategory] || {};
      const dgBucket = catBucket[displayGroup] = catBucket[displayGroup] || {};
      const acc = dgBucket[m] = dgBucket[m] || { wages: 0, bonuses: 0, commissions: 0, payroll_taxes: 0, benefits: 0 };
      /* SEVERANCE LANDS ON THE DEPARTMENT'S WAGE LEAF -- it is not given a line of its own.
         (A separate income-statement line for severance is a different arc.) COBRA needs no
         term here: it is already inside `ben`. */
      acc.wages         = r2(acc.wages         + sal + (severance || 0));
      // Commission ship 4 (K5): an accruing company's income statement carries the commission EXPENSED; otherwise the PAID one.
      const isComm = commAccrualOn ? (slot.commissionExpense || 0) : commission;
      if (COMMISSION_LINE_KEYS.has(buildPayrollIsLineKey(isCategory, displayGroup, 'commissions'))) {
        acc.bonuses     = r2(acc.bonuses     + bonus);        // 2c (R14): this group has its own Commissions line
        acc.commissions = r2(acc.commissions + isComm);
      } else {
        acc.bonuses       = r2(acc.bonuses       + bonus + isComm); // commission folded
      }
      acc.payroll_taxes = r2(acc.payroll_taxes + tax);
      acc.benefits      = r2(acc.benefits      + ben);
        // ★ HC UNIFICATION: the head tally that used to live here is GONE. Heads are
        // derived ONCE, after this loop, by deriveHeadcount() from `ed` — the single
        // source of truth — so this branch aggregates MONEY only. (The previous
        // payroll-hc fix incremented dd[dept][m].hc here; that private tally is what
        // the unification removes, not the behaviour: deriveHeadcount's PAYROLL-RUN
        // view reproduces exactly the same set of employee-months, because it applies
        // the same `sal > 0 || commission > 0` predicate this branch's shared gate
        // above encodes.)
    } else {
      // ★ B2: in UNMAPPED mode every employee aggregates into the one explicit
      // 'unmapped' department. In LEGACY and MAPPED modes this is `employee.department`
      // exactly as before.
      // ★ THE MIXED-TENANT GUARD. Recorded HERE — before the `if (!D) return` below,
      // which fires for a NULL department (dd has no such key) and would otherwise
      // swallow the very case this exists to name.
      if (!unmappedDeptMode && !warnedOrphanEmps.has(employee.id)) {
        warnedOrphanEmps.add(employee.id);
        // forecast months only: a historical engine value never reaches the IS anyway
        // (runFullModel merges `m > cutoff` only), so quoting the full window would
        // overstate what is actually lost.
        let fcAmount = 0;
        const em = ed[employee.id] || {};
        for (const mm of MONTHS) {
          if (CUTOFF && mm <= CUTOFF) continue;
          const s2 = em[mm];
          if (s2) fcAmount = r2(fcAmount + (s2.tot || 0));
        }
        orphanDeptWarnings.push({
          reason: 'department_not_mapped',
          employeeId: employee.id,
          name: employee.name || null,
          department: employee.department || null,
          forecastAmount: fcAmount,
        });
        console.warn(`[computePayroll] ${employee.name || '#' + employee.id} ${employee.department
          ? `is in department "${employee.department}", which is not in this company's department mappings`
          : 'has no department set'} — $${fcAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} of forecast payroll is not reaching a line the operator chose. Depending on the department's name it is either missing from the P&L entirely, or booked to a fallback line on the legacy chart. Map the department in Step 5c, or correct the employee's department.`);
      }
      const D = dd[unmappedDeptMode ? UNMAPPED_DEPT : employee.department]?.[m];
      if (!D) return;
      D.s=r2(D.s+sal); D.b=r2(D.b+bonus); D.c=r2(D.c+commission); D.t=r2(D.t+tax); D.ss=r2(D.ss+ss); D.med=r2(D.med+med);
      D.futa=r2(D.futa+futa); D.suta=r2(D.suta+suta); D.ben=r2(D.ben+ben); D.tot=r2(D.tot+tot);
      // ★ HC UNIFICATION: no `D.hc+=1` here either — see the modern branch above.
    }
  });

  /* ⛔ REMOVED 2026-09-04 — the dd -> cd and cd -> g rolls. Both walked the four Semitek
   * categories and their six department names; on every live tenant they summed all-zero
   * buckets into all-zero totals (measured: `g`'s ten money fields are $0 in all 36 months
   * on cid 3/8/36/40, PRE and POST alike). Their one live effect was the mis-routing above. */

  // ★ B2 FAIL-LOUD, the company total. ★ SINCE 2026-09-04 THIS FOLD IS THE ONLY THING THAT
  // PUTS MONEY IN `g` — the roll that used to feed it went with the Semitek categories, and
  // it was summing zeros anyway. In UNMAPPED mode `dd.unmapped` holds real money, so fold it
  // in explicitly: the company total must never under-report payroll just because we cannot
  // categorise it. (No-op in MAPPED mode — the bucket does not exist there, and `g`'s money
  // fields stay $0 exactly as they did before the removal.) The is_category BREAKDOWN stays
  // honestly empty; only the TOTAL is made whole.
  if (unmappedDeptMode) {
    MONTHS.forEach(m => {
      const D = dd[UNMAPPED_DEPT]?.[m], G = g[m];
      if (!D || !G) return;
      G.s=r2(G.s+D.s); G.b=r2(G.b+D.b); G.c=r2(G.c+D.c); G.t=r2(G.t+D.t); G.ss=r2(G.ss+D.ss); G.med=r2(G.med+D.med);
      G.futa=r2(G.futa+D.futa); G.suta=r2(G.suta+D.suta); G.ben=r2(G.ben+D.ben); G.tot=r2(G.tot+D.tot);
    });
  }

  // ── HC UNIFICATION: derive every head count ONCE, from `ed` ──────────────────
  // `ed` is the single source of truth (per person, per month). deriveHeadcount
  // returns both views; the engine's own consumers want PAYROLL-RUN (per-seat vendor
  // billing bills a seat for anyone the run touches), and the full result is passed
  // through on `headcount` so the pages read it instead of re-deriving.
  //
  // ★ Departments come from department_mappings ONLY — is_category is not consulted.
  // The dd/cd/g `hc` fields below are now a PROJECTION of that one derivation, not a
  // parallel tally: dd is the per-department count, cd is a DISPLAY GROUPING (a sum of
  // its departments), and g is the deriver's own TOTAL — deliberately NOT the sum of
  // cd, so an employee in an unmapped or 'Other'-category department cannot vanish
  // from the company total the way the old cd-rollup let them.
  // ★ B2 FAIL-LOUD RECORD — one entry for the tenant-level condition, naming how many
  // employees and which departments are affected. ⓘ THE READER IS THE PAYROLL PAGE'S
  // MODEL-HEALTH PANEL (PayrollPage, beside sutaWarnings / missingAnchorWarnings /
  // orphanDeptWarnings), NOT the pre-flight: preflightCheck.js references none of these
  // arrays. This comment claimed the pre-flight until it was measured — corrected here
  // rather than left to send the next reader to a file that never mentions it.
  if (unmappedDeptMode && emps.length > 0) {
    const depts = [...new Set(emps.map(e => e.department).filter(Boolean))].sort();
    missingDeptMappings.push({
      reason: 'no_department_mappings',
      employeeCount: emps.length,
      departments: depts,
    });
    console.warn(`[computePayroll] This company has ${emps.length} employees but NO department_mappings — payroll is aggregated into a single "unmapped" department and reaches no income-statement category. Departments seen: ${depts.join(', ') || '(none)'}. Complete onboarding Step 5c.`);
  }

  const headcount = deriveHeadcount({ ed, employees: emps, months: MONTHS, deptMappings });
  const hcRun = headcount.payrollRun;
  Object.keys(dd).forEach(dept => {
    MONTHS.forEach(m => { dd[dept][m].hc = hcRun.byDeptName[dept]?.[m] || 0; });
  });
  // ★ B2: in UNMAPPED mode dd is keyed by the single 'unmapped' bucket while
  // byDeptName is keyed by each employee's RAW department string, so the loop above
  // finds nothing. Take the heads from the resolver's own unmapped bucket instead —
  // the same derivation, just the matching key.
  if (unmappedDeptMode && dd[UNMAPPED_DEPT]) {
    MONTHS.forEach(m => { dd[UNMAPPED_DEPT][m].hc = hcRun.byKey[UNMAPPED_DEPT]?.[m] || 0; });
    MONTHS.forEach(m => { g[m].hc = hcRun.total[m] || 0; });
  }
  /* ⛔ REMOVED 2026-09-04 — the `cd` head-count projection. It summed the six Semitek
   * department names, which no live tenant has, into the four Semitek category ids.
   * `dd` (above) and `g` (below) still carry every head; nothing is lost. */
  MONTHS.forEach(m => { g[m].hc = hcRun.total[m] || 0; });

  // Build IS line mapping
  const isLines = {};
  /* ⛔⛔ REMOVED 2026-09-04 — THE CATEGORY-LEVEL EMISSION BLOCK. It wrote `cd` (the Semitek
   * category totals) onto real income-statement keys through CAT_MAP, for EVERY month.
   *
   * WHAT WENT WITH IT, measured on cid 3/8/36/40 so a future reader need not re-derive it:
   *   · NO isLines KEY disappears. 28 keys before, 28 after. The twelve "middle-drop
   *     collapsed" keys (cogs_/ga_/rd_ x wages|bonuses|payroll_taxes|benefits) are also
   *     emitted by the cdByDg block below, which owns their forecast months.
   *   · 324 HISTORICAL month-entries per tenant DO disappear — 12 keys x 27 closed months,
   *     every one of them $0.00, and every one of them BEFORE the cutoff, so the
   *     `if (m > cutoff)` merge in runFullModel could never have carried them anyway.
   *   · In UNMAPPED mode (no department_mappings) `cdByDg` is empty, so this block was the
   *     only emitter: isLines goes from 12 all-zero keys to 0 keys. ★ ACCEPTED AND STATED:
   *     the IS rows still render from buildISLines, `missingDeptMappings` still fires, and
   *     `g` still carries the true company total via its own explicit fold. Emitting twelve
   *     zeros was a side effect of this block, not a designed signal.
   *   · The shape-blind PRE/POST diff moved $0.00 at this layer and through the full
   *     runFullModel sweep on all four tenants. */

  // Session 3b: modern dept-mappings emission. Per-(catKey, displayGroup)
  // keys via payrollKeys helper. Forecast-only gate (m > CUTOFF) prevents
  // historical double-count during the Session 3b → Session 4 transition
  // window. Historical values for these keys will be populated by Session 4's
  // is_actuals re-keying migration; until then, historical months show $0
  // for per-dg keys while the catch-all is_actuals overlay carries the
  // historical section total via orphan-leaf rendering.
  const PAYTYPES_OUT = ['wages', 'bonuses', 'commissions', 'payroll_taxes', 'benefits'];
  for (const [catKey, dgMap] of Object.entries(cdByDg)) {
    for (const [dg, monthMap] of Object.entries(dgMap)) {
      for (const paytype of PAYTYPES_OUT) {
        const key = buildPayrollIsLineKey(catKey, dg, paytype);
        if (paytype === 'commissions' && !COMMISSION_LINE_KEYS.has(key)) continue;   // 2c: folded groups emit no commissions key
        isLines[key] = isLines[key] || {};
        MONTHS.forEach(m => {
          if (m <= CUTOFF) return;
          const slot = monthMap[m];
          if (slot) isLines[key][m] = r2(slot[paytype]);
        });
      }
    }
  }

  // ⓘ `cd` is returned EMPTY and that is deliberate (see its removal note above): keeping the
  //   key means no consumer needs an existence check and no capture loses a top-level key.
  //   PayrollPage reads it as `payrollData?.cd || {}`.
  const result = { ed, dd, cd, g, isLines, sutaWarnings, fedTaxWarnings, missingAnchorWarnings, missingDeptMappings, orphanDeptWarnings, severanceWarnings, headcount };
  /* Bi-weekly + accrues ONLY (step 3b): the balance the linked balance-sheet account carries at each month-end.
       accruedPayroll     { 'YYYY-MM': accrued wages (+ the accrued employer tax when the accrual covers wages_and_taxes) }
       accruedPayrollTax  { 'YYYY-MM': the accrued employer tax }   -- only when taxes are covered
     Every month of the window carries a key. A schedule that cannot make a calendar yields NO series (and
     payScheduleError says why); the keys are absent for every other company. */
  if (accrualOn) {
    if (!noPayCalendar) {
      const accruedPayroll = {};
      MONTHS.forEach(m => { accruedPayroll[m] = accrualTaxOn ? r2(accruedWagesTotal[m] + accruedTaxTotal[m]) : accruedWagesTotal[m]; });
      result.accruedPayroll = accruedPayroll;
      if (accrualTaxOn) result.accruedPayrollTax = accruedTaxTotal;
    }
    if (payScheduleError) result.payScheduleError = payScheduleError;
  }
  /* Commission ship 4 -- an accruing company ONLY (every other company's result gains no key):
       accruedCommissions       { 'YYYY-MM': the balance } -- the close month (the computed close) and every forecast month
       accruedCommissionsClose  the computed close balance (R9 + B1) -- P24 compares the linked account's GL close with it */
  if (commAccrualOn) {
    const accruedCommissions = {};
    let bal = commCloseTotal;
    if (CUTOFF && MONTHS.includes(CUTOFF)) accruedCommissions[CUTOFF] = bal;
    MONTHS.forEach(m => {
      if (isClosedMonth(m)) return;
      bal = r2(bal + commExpensedTotal[m] - commPaidTotal[m]);
      accruedCommissions[m] = bal;
    });
    result.accruedCommissions = accruedCommissions;
    result.accruedCommissionsClose = commCloseTotal;
  }
  return result;
}

